import * as React from 'react'

import {
  Avatar,
  Box,
  Button,
  Prose,
  Text,
  useBreakpoint,
} from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  PerformanceAgreementQuestionCommentFragment,
  PerformanceAgreementQuestionnaireAnsweredQuestionFragment,
  PerformanceAgreementQuestionnaireSubjectFragment,
  PerformanceProcessAgreementsQuestionnaireAddManagerCommentToQuestionDocument,
  PerformanceProcessAgreementsQuestionnaireAddTargetCommentToQuestionDocument,
} from 'generated/resources/graphql'
import { keyBy, mapValues } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ActionInput } from 'modules/performance_process/components/common/ActionInput'
import { AnonymousAvatar } from 'modules/performance_process/components/common/AnonymousAvatar'

import { EmptyState } from './_EmptyState'

type AgreeementRole = 'manager' | 'target'

const TextAnswerDisplay = ({ text }: { text: string }) => {
  const { canUseRichTextEditor, loading } = useRichTextEditor()

  if (loading) return null

  if (canUseRichTextEditor) {
    return <RichTextDisplay content={text} />
  }

  return (
    <Text.Normal overflowWrap>
      <Prose markdown>{text}</Prose>
    </Text.Normal>
  )
}

export const AgreementQuestionnaireAnswer: React.FC<{
  answeredQuestion?: PerformanceAgreementQuestionnaireAnsweredQuestionFragment
  subject?: PerformanceAgreementQuestionnaireSubjectFragment
  comment?: PerformanceAgreementQuestionCommentFragment
  canComment: boolean
  agreementId: string
  role: AgreeementRole
}> = ({ answeredQuestion, subject, comment, canComment, agreementId, role }) => {
  const question = answeredQuestion?.question
  return (
    <Box
      paddingX="s24"
      paddingY="s16"
      background="grey200"
      borderRadius={{ all: 'abs016' }}
      height="full"
    >
      <Box gap="s16" height="full">
        {subject ? <AgreementQuestionnaireSubject subject={subject} /> : <AnonymousAvatar />}

        <Box height="full">
          <AnswerContent answeredQuestion={answeredQuestion} subject={subject} />
        </Box>

        {question && (
          <Box gap="s8">
            <AgreementQuestionnaireComment
              comment={comment}
              canComment={canComment}
              questionId={question.uuid}
              agreementId={agreementId}
              role={role}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const AgreementQuestionnaireComment: React.FC<{
  agreementId: string
  questionId: string
  role: AgreeementRole
  comment?: PerformanceAgreementQuestionCommentFragment
  canComment: boolean
}> = ({ agreementId, questionId, role, comment, canComment }) => {
  const [editMode, setEditMode] = React.useState(false)
  const [newComment, setNewComment] = React.useState('')
  const textComment = comment?.text || ''

  const [updateManagerComment, { loading: managerLoading }] = useMutation(
    PerformanceProcessAgreementsQuestionnaireAddManagerCommentToQuestionDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.upsertManagerQuestionCommentPerformanceAgreement,
    }
  )

  const [updateTargetComment, { loading: targetLoading }] = useMutation(
    PerformanceProcessAgreementsQuestionnaireAddTargetCommentToQuestionDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.upsertSelfQuestionCommentPerformanceAgreement,
    }
  )

  const handleEdit = () => {
    setNewComment(textComment ?? '')
    setEditMode(true)
  }

  const cancelEdit = () => {
    setEditMode(false)
    setNewComment('')
  }

  const changeManagerComment = async () => {
    const response = await updateManagerComment({
      variables: {
        agreementId,
        questionId,
        text: newComment,
      },
    })
    handleResult(response, () => null)
  }

  const changeTargetComment = async () => {
    const response = await updateTargetComment({
      variables: {
        agreementId,
        questionId,
        text: newComment,
      },
    })
    handleResult(response, () => null)
  }

  const updateComment = async () => {
    const updatePromise = role === 'manager' ? changeManagerComment : changeTargetComment
    await updatePromise()
    setEditMode(false)
  }

  const loading = managerLoading || targetLoading

  return (
    <Box gap="s8">
      {(textComment !== '' || canComment) && (
        <Text.Header3 weight="semibold">
          {i18n.t('performance_process.agreements.comments.title')}
        </Text.Header3>
      )}
      {textComment !== '' && !editMode && (
        <Box flexDirection="row">
          <Box width="full">
            <Text.Normal>{textComment}</Text.Normal>
          </Box>

          {canComment && <Button onClick={handleEdit} type="secondary" iconName="pencil" />}
        </Box>
      )}

      {canComment && (textComment === '' || editMode) && (
        <ActionInput
          autofocus={editMode}
          onCancel={() => cancelEdit()}
          placeholder={i18n.t('performance_process.agreements.comments.add_comment')}
          disabled={loading}
          onChange={(value) => setNewComment(value)}
          value={newComment}
          onSubmit={() => updateComment()}
        />
      )}
    </Box>
  )
}

const AnswerContent: React.FC<{
  answeredQuestion?: PerformanceAgreementQuestionnaireAnsweredQuestionFragment
  subject?: PerformanceAgreementQuestionnaireSubjectFragment
}> = ({ answeredQuestion, subject }) => {
  const { isMobile } = useBreakpoint()
  const question = answeredQuestion?.question
  const answer = answeredQuestion?.answer
  const unanswered = question && !answer
  const ratingScale = mapValues(keyBy(question?.scale || [], 'value'), 'text')
  if (!question) {
    return null
  }

  if (unanswered) {
    return <EmptyState subject={subject} />
  }

  switch (question.answerType) {
    case 'rating':
      return (
        <>
          {answer?.answerRating && (
            <Box gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Box
                  paddingY="s2"
                  paddingX="s8"
                  background="grey800"
                  borderRadius={{ all: 'abs008' }}
                >
                  <Text.Normal
                    color="grey000White"
                    size={isMobile ? '100' : '200'}
                    weight="semibold"
                  >
                    {answer.answerRating.value}
                  </Text.Normal>
                </Box>
                <Text.Normal color="grey800" weight="medium">
                  {answer.answerRating.value && ratingScale[answer.answerRating.value]}
                </Text.Normal>
              </Box>
              <Box flexDirection="column">
                <TextAnswerDisplay text={answer.answerRating.comment ?? ''} />
              </Box>
            </Box>
          )}
        </>
      )

    case 'text':
      return <TextAnswerDisplay text={answer?.answerText ?? ''} />

    case 'number':
      return (
        <Box flexDirection="column">
          <Text.Normal size="300">{answer?.answerInt}</Text.Normal>
        </Box>
      )

    case 'multiple_choice':
      const choices = answer?.answerChoice || []
      return (
        <Box flexDirection="column">
          <Text.Normal size="300">{choices.join(', ')}</Text.Normal>
        </Box>
      )

    default:
      const _exhaustiveCheck: never = question.answerType
      throw new Error(`Unhandled question type: ${_exhaustiveCheck}`)
  }
}

const AgreementQuestionnaireSubject: React.FC<{
  subject: PerformanceAgreementQuestionnaireSubjectFragment
}> = ({ subject }) => {
  return (
    <Box flexDirection="row" alignItems="center" gap="s16">
      <Avatar size="s32" url={buildUrl(subject.avatar)} label={subject.fullName ?? ''} />
      <Text.Normal weight="semibold" size="300">
        {subject.fullName}
      </Text.Normal>
    </Box>
  )
}
